import { Smartphone, UserCheck, Wallet } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Stepper, type StepperStep } from "@/components/ui/stepper";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import { useAuthStore } from "@/stores/auth-store";
import { PairSignerDevice } from "@/features/admin/pair-signer-device";

// ---------------------------------------------------------------------------
// /signer/get-started (ALL-5137)
//
// Where a transaction_signer lands after accepting an invite, and where the
// signer invite email points (context ALL-5138). Three steps, no new
// permissions: the Android download is the existing authenticated route
// (/v1/downloads/signer-app/android/latest.apk, SignerAppDownloadController),
// which any signed-in human may use. Step 2 (D714, ALL-5472) is the signer's
// own "Pair my phone": POST /v1/signer/pairings/self after an AAL2 step-up,
// bound by the backend to the signed-in principal, so no admin is needed for
// the first phone. A replacement phone still needs two admins (D707). The
// signed-in email stays on the page for the admin-approval fallback.
// ---------------------------------------------------------------------------

export default function SignerGetStartedPage() {
  const t = useMobileSigningLocale().signerStart;
  const email = useAuthStore((s) => s.user?.email ?? s.session?.email ?? "");

  // The three steps, in the order the page presents them. Nothing on this page
  // knows how far a signer has actually got — the install and the pairing both
  // happen outside the browser — so the Stepper is given no per-step status and
  // claims no completed step; it only marks where the onboarding starts. The
  // step numbers live in the Stepper's own circles, never in the copy.
  const steps: StepperStep[] = [
    { label: t.step1Title },
    { label: t.step2Title },
    { label: t.step3Title },
  ];

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-6" data-testid="signer-get-started">
      <div>
        <h1 className="text-xl font-semibold text-ink">{t.title}</h1>
        <p className="mt-1 text-sm text-muted">{t.intro}</p>
      </div>

      <Stepper steps={steps} current={0} />

      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle className="flex items-center gap-2">
            <Smartphone size={18} aria-hidden="true" />
            {t.step1Title}
          </CardTitle>
          <p className="text-sm text-muted">{t.step1Body}</p>
          <div>
            <a
              href="/get-app/android"
              className={buttonVariants({ variant: "primary", size: "sm" })}
              data-testid="signer-get-started-android-link"
            >
              {t.step1Action}
            </a>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle className="flex items-center gap-2">
            <UserCheck size={18} aria-hidden="true" />
            {t.step2Title}
          </CardTitle>
          <p className="text-sm text-muted">{t.step2Body}</p>
          <PairSignerDevice principalId="" isHuman canManage mode="self" />
          {email ? (
            <p className="text-sm text-ink">
              {t.step2EmailLabel}{" "}
              <span className="font-mono" data-testid="signer-get-started-email">
                {email}
              </span>
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle className="flex items-center gap-2">
            <Wallet size={18} aria-hidden="true" />
            {t.step3Title}
          </CardTitle>
          <p className="text-sm text-muted">{t.step3Body}</p>
        </CardBody>
      </Card>
    </div>
  );
}
